import { useState, useEffect } from "react";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { presetToRange } from "@/components/reports/shared";
import { RangePicker } from "@/components/reports/RangePicker";
import { TodaySnapshot } from "@/components/reports/TodaySnapshot";
import { EndOfDaySection } from "@/components/reports/sections/EndOfDaySection";
import { DailyComparisonSection } from "@/components/reports/sections/DailyComparisonSection";
import { TipsByEmployeeSection } from "@/components/reports/sections/TipsByEmployeeSection";
import { WagesByEmployeeSection } from "@/components/reports/sections/WagesByEmployeeSection";
import { EmployeeDetailsSection } from "@/components/reports/sections/EmployeeDetailsSection";
import { RevenueBreakdownSection } from "@/components/reports/sections/RevenueBreakdownSection";
import { TenderTypesSection } from "@/components/reports/sections/TenderTypesSection";
import { PerkRedemptionsSection } from "@/components/reports/sections/PerkRedemptionsSection";
import { CardFeeRecoverySection } from "@/components/reports/sections/CardFeeRecoverySection";
import { DeliveryDispatchesSection } from "@/components/reports/sections/DeliveryDispatchesSection";

const REPORT_OPTIONS = [
  { value: "eod", label: "End of Day", isSingleDay: true },
  { value: "comparison", label: "Daily Comparison", isSingleDay: true },
  { value: "tips", label: "Tips per Employee", isSingleDay: false },
  { value: "wages", label: "Wages (Tips Included)", isSingleDay: false, isWagesEndpoint: true },
  { value: "employee", label: "Employee Details", isSingleDay: false },
  { value: "revenue", label: "Revenue Breakdown", isSingleDay: false },
  { value: "tenders", label: "Sales by Tender Type", isSingleDay: false },
  { value: "perks", label: "Loyalty Perk Redemptions", isSingleDay: false },
  { value: "card_fee", label: "Card Fee Recovery", isSingleDay: false },
  { value: "dispatches", label: "Delivery Dispatches", isSingleDay: false },
];

const ReportBody = ({ report, filter }) => {
  switch (report.value) {
    case "eod":
      return <EndOfDaySection filter={filter} />;
    case "comparison":
      return <DailyComparisonSection filter={filter} />;
    case "tips":
      return <TipsByEmployeeSection filter={filter} />;
    case "wages":
      return <WagesByEmployeeSection filter={filter} />;
    case "employee":
      return <EmployeeDetailsSection filter={filter} />;
    case "revenue":
      return <RevenueBreakdownSection filter={filter} />;
    case "tenders":
      return <TenderTypesSection filter={filter} />;
    case "perks":
      return <PerkRedemptionsSection filter={filter} />;
    case "card_fee":
      return <CardFeeRecoverySection filter={filter} />;
    case "dispatches":
      return <DeliveryDispatchesSection filter={filter} />;
    default:
      return null;
  }
};

const ReportsPage = () => {
  const [activeReport, setActiveReport] = useState("eod");
  const active = REPORT_OPTIONS.find((o) => o.value === activeReport);

  // Single shared filter for whichever report is selected. Reset start/end
  // sensibly when toggling between single-day and range reports.
  const [filter, setFilter] = useState(() => {
    const r = presetToRange("daily");
    return {
      preset: "daily",
      start: r.start,
      end: r.end,
      startTime: "",
      endTime: "",
      weekdays: [],
      comparePrev: false,
    };
  });

  useEffect(() => {
    // When switching between single-day and range reports, normalise the
    // preset so the picker reflects something useful.
    setFilter((prev) => {
      if (active.isSingleDay && (prev.preset === "weekly" || prev.preset === "monthly")) {
        const r = presetToRange("daily");
        return { ...prev, preset: "daily", start: r.start, end: r.end };
      }
      if (!active.isSingleDay && prev.preset === "daily") {
        const r = presetToRange("weekly");
        return { ...prev, preset: "weekly", start: r.start, end: r.end };
      }
      return prev;
    });
  }, [active]);

  return (
    <div className="min-h-screen bg-background" data-testid="reports-page">
      <Sidebar />
      <main className="main-content p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-medium text-foreground">Reports</h1>
            <p className="text-muted-foreground">Sales, payroll and operations analytics</p>
          </div>
        </div>

        {/* Today's Snapshot — always visible header summary */}
        <TodaySnapshot />

        {/* Report selector + shared range picker */}
        <Card data-testid="report-selector-card">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <CardTitle className="font-heading text-xl">Report Viewer</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Pick a report and filter window — date range, time-of-day, days of week.
                </p>
              </div>
              <div className="w-full md:w-72">
                <Select value={activeReport} onValueChange={setActiveReport}>
                  <SelectTrigger data-testid="report-select-trigger">
                    <SelectValue placeholder="Choose a report" />
                  </SelectTrigger>
                  <SelectContent>
                    {REPORT_OPTIONS.map((opt) => (
                      <SelectItem
                        key={opt.value}
                        value={opt.value}
                        data-testid={`report-select-option-${opt.value}`}
                      >
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <RangePicker
              value={filter}
              onChange={setFilter}
              isSingleDay={active.isSingleDay}
              testIdPrefix="reports-rp"
            />
            <ReportBody report={active} filter={filter} />
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ReportsPage;
